import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api, asArray } from "@/lib/api";

const CityContext = createContext({ city: "", setCity: () => {}, cities: [], byState: [], states: [] });
const KEY = "dmv_city";

export function CityProvider({ children }) {
  const [city, setCityState] = useState(() => { try { return localStorage.getItem(KEY) || ""; } catch { return ""; } });
  const [cities, setCities] = useState([]);

  useEffect(() => {
    let alive = true;
    // The full state → city list ships with the app too, so dropdowns are complete even if the
    // server still has an older/shorter list. Server cities (incl. ones added by admins) are merged in.
    const withBundled = async (server) => {
      if (server.length >= 1000) return server;
      try {
        const mod = await import(/* webpackChunkName: "india-cities" */ "@/data/india_cities.json");
        const data = mod.default || mod;
        const seen = new Set(server.map((c) => `${c.name}|${c.state || ""}`.toLowerCase()));
        const extra = [];
        Object.entries(data).forEach(([state, list]) => (list || []).forEach((name) => {
          const k = `${name}|${state}`.toLowerCase();
          if (!seen.has(k)) { seen.add(k); extra.push({ name, state }); }
        }));
        return [...server, ...extra];
      } catch { return server; }
    };
    api.get("/cities").then((r) => asArray(r.data).filter((c) => c && typeof c.name === "string"))
      .catch(() => [])
      .then(withBundled)
      .then((list) => { if (alive) setCities(list); });
    return () => { alive = false; };
  }, []);

  // grouped by state, derived locally (one request instead of two)
  const byState = useMemo(() => {
    const m = new Map();
    for (const c of cities) {
      const s = c.state || "Other";
      if (!m.has(s)) m.set(s, []);
      m.get(s).push(c.name);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([state, list]) => ({ state, cities: list.sort((a, b) => a.localeCompare(b)) }));
  }, [cities]);
  const states = useMemo(() => byState.map((s) => s.state), [byState]);

  const setCity = (c) => {
    setCityState(c || "");
    try { if (c) localStorage.setItem(KEY, c); else localStorage.removeItem(KEY); } catch {}
  };

  return <CityContext.Provider value={{ city, setCity, cities, byState, states }}>{children}</CityContext.Provider>;
}

export const useCity = () => useContext(CityContext);
